import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button, DrawerHeader, DrawerBody, DrawerFooter, TextField, Drawer, Link
} from '@one-thd/sui-atomic-components';
import {
  QueryProvider, extend, useDataModel,
  arrayOf,
  number as numberType,
  string as stringType,
  customType,
  params, hoist
} from '@thd-nucleus/data-sources';
import { ProductListContext } from '../ProductListContext';

export const EditGroup = ({ groupId, groupName: initialGroupName, groupOrder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [groupName, setGroupName] = useState(initialGroupName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessaging, setErrorMessaging] = useState('');

  const { list, onEditSuccess } = useContext(ProductListContext);

  const [listOperations] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    fetchPolicy: 'no-cache',
    context: { withAuth: true },
    lazy: true
  });

  useEffect(() => {
    setGroupName(initialGroupName || '');
  }, [initialGroupName]);

  const handleClose = () => {
    setIsOpen(false);
    setGroupName(initialGroupName || '');
    setErrorMessaging('');
  };

  const handleOpen = () => {
    setIsOpen(true);
    // Analytics: Edit Group overlay opened
    window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.edit-group');
  };

  const handleNameValidation = (value) => {
    if (!value || !value.trim()) {
      setErrorMessaging('Group name is required');
      return false;
    }
    setErrorMessaging('');
    return true;
  };

  const handleOnChange = (event) => {
    const { value } = event.target;
    setGroupName(value);
    if (errorMessaging) {
      handleNameValidation(value);
    }
  };

  const handleSubmit = async (submitEvent) => {
    if (submitEvent) {
      submitEvent.preventDefault();
    }

    if (!handleNameValidation(groupName)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await listOperations({
        variables: {
          operationName: 'Update',
          id: list.id,
          groups: [{
            groupId,
            groupName: groupName.trim(),
            groupOrder: groupOrder || '1'
          }]
        }
      });

      // Analytics: Group name successfully edited
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.edit-group-success');

      onEditSuccess({});
      handleClose();
    } catch (error) {
      // Error updating group - could show user feedback here
      setErrorMessaging('Failed to update group name');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <>
      <Button
        data-testid="edit-group-drawer-button"
        variant="ghost"
        onClick={handleOpen}
      >
        Edit Group Settings
      </Button>
      <Drawer
        open={isOpen}
        onClose={handleClose}
      >
        <DrawerHeader title="Edit Group" onClose={handleClose} />

        <DrawerBody>
          <form
            className="sui-grid sui-gap-6"
            onSubmit={handleSubmit}
          >
            <TextField
              id="group-name"
              label="Group Name"
              placeholder="Enter Group Name"
              type="text"
              onBlur={() => { handleNameValidation(groupName); }}
              value={groupName}
              fullWidth
              onChange={handleOnChange}
              onKeyPress={handleKeyPress}
              status={errorMessaging ? 'error' : null}
              statusMessage={errorMessaging || ''}
              InputProps={{
                inputProps: {
                  'data-testid': 'edit-name-input'
                },
              }}
            />
          </form>
        </DrawerBody>

        <DrawerFooter position="center">
          <div className="sui-flex sui-flex-col sui-gap-2">
            <Button
              data-testid="edit-group-submit-button"
              variant="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={Boolean(!groupName.trim() || isSubmitting || errorMessaging)}
              type="submit"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            <Button
              data-testid="edit-group-cancel-button"
              variant="secondary"
              fullWidth
              onClick={handleClose}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </>
  );
};

EditGroup.dataModel = extend(
  {
    listOperations: params({
      operationName: customType('Operation').enum(['Update'], 'Update'),
      id: stringType().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType()
      })),
      name: stringType(),
      listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
    }).mutation().shape({
      id: stringType(),
      groups: arrayOf(customType('Group').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType(),
        status: stringType(),
        items: arrayOf(customType('Item').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          status: stringType(),
          intent: stringType(),
          intentId: stringType()
        }))
      }))
    })
  }
);

EditGroup.propTypes = {
  groupId: PropTypes.string.isRequired,
  groupName: PropTypes.string.isRequired,
  groupOrder: PropTypes.number.isRequired
};
